// @ts-nocheck
import { useState } from 'react';
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AI_AGENT_FIELD_LIMITS } from '@/utilities/constant';
import { getAiAgentDescriptionCharLimit } from '@/utilities/aiAgentAcl';
import CollapsibleSection from './CollapsibleSection';
import HeadingInfo from './HeadingInfo';

const { MCP_SERVERS_MAX_ITEMS, MCP_NAME_MAX, MCP_URL_MAX } = AI_AGENT_FIELD_LIMITS;

const errClass = (hasErr: boolean) =>
  hasErr ? 'border-destructive focus-visible:ring-destructive' : '';


const clip = (value: string, max: number, id: string, label: string) => {
  if (value.length > max) {
    toast.error(`${label} can be max ${max} characters`, { id });
    return value.slice(0, max);
  }
  return value;
};


const FieldMeta = ({ error, count, max }: { error?: string; count: number; max: number }) => (
  <div className="flex items-start justify-between gap-2">
    <p className="text-xs text-destructive">
      {error || (count >= max ? `Max ${max} characters reached` : '')}
    </p>
    <span className="shrink-0 text-xs text-muted-foreground">
      {count}/{max}
    </span>
  </div>
);

const isValidUrl = (v: string) => {
  try {
    const u = new URL(v.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

const genId = () =>
  (crypto.randomUUID?.() as string) || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export default function McpSection({ value, onChange }: any) {
  const servers = value || [];
 
  const [openId, setOpenId] = useState<string | null>(null);
  const [attemptedId, setAttemptedId] = useState<string | null>(null);
  const descLimit = getAiAgentDescriptionCharLimit();

  const getServerErrors = (server: any, showRequired: boolean) => {
    const e: any = {};
    const n = (server.name || '').trim();
    if (!n) {
      if (showRequired) e.name = 'MCP server name is required';
    } else if (n.length > MCP_NAME_MAX) {
      e.name = `Name can be max ${MCP_NAME_MAX} characters`;
    }
    const u = (server.url || '').trim();
    if (!u) {
      if (showRequired) e.url = 'MCP server URL is required';
    } else if (u.length > MCP_URL_MAX) {
      e.url = `URL can be max ${MCP_URL_MAX} characters`;
    } else if (!isValidUrl(u)) {
      e.url = 'Enter a valid URL starting with http:// or https://';
    }
    if ((server.description || '').length > descLimit) {
      e.description = `Description can be max ${descLimit} characters`;
    }
    return e;
  };

  const addServer = () => {
    if (servers.length >= MCP_SERVERS_MAX_ITEMS) {
      toast.error(`You can add up to ${MCP_SERVERS_MAX_ITEMS} MCP servers`);
      return;
    }
    const current = servers.find((s: any) => s.id === openId);
    if (current && !current.name?.trim() && !current.url?.trim()) {
      setAttemptedId(current.id);
      toast.error('Fill the current server and click the tick first');
      return;
    }
    const item = { id: genId(), name: '', url: '', description: '' };
    onChange([...servers, item]);
    setOpenId(item.id);
  };

  const updateServer = (id: string, patch: any) =>
    onChange(servers.map((s: any) => (s.id === id ? { ...s, ...patch } : s)));

  const removeServer = (id: string) => {
    onChange(servers.filter((s: any) => s.id !== id));
    if (openId === id) setOpenId(null);
  };

  // Tick: validate, then move the server into the listing.
  const commitServer = (server: any) => {
    const errs = getServerErrors(server, true);
    const first = errs.name || errs.url || errs.description;
    if (first) {
      setAttemptedId(server.id);
      toast.error(first);
      return;
    }
    setAttemptedId(null);
    setOpenId(null);
  };

  return (
    <CollapsibleSection title="MCP" info="Connect external MCP (Model Context Protocol) servers so the AI can call outside tools or data sources during the conversation.">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">
              MCP Servers
              <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                ({servers.length}/{MCP_SERVERS_MAX_ITEMS})
              </span>
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Configure one or more MCP (Model Context Protocol) servers for this agent.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={addServer} disabled={servers.length >= MCP_SERVERS_MAX_ITEMS}>
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>

        {servers.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">No MCP servers added yet.</p>
        )}

        {servers.map((server: any, index: number) => {
          const errs = getServerErrors(server, attemptedId === server.id);
          return server.id === openId ? (
            <div key={server.id} className="space-y-3 rounded-md border border-border p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-foreground">Server {index + 1}</p>
                <span className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-emerald-600 hover:text-emerald-700"
                    onClick={() => commitServer(server)}
                    title="Done"
                    aria-label="Done"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => removeServer(server.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </span>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label>
                    Name <span className="text-destructive">*</span>
                  </Label>
                  <HeadingInfo text="The name of this MCP tool server. Example: order-lookup." />
                </div>
                <Input
                  placeholder="e.g. order-lookup"
                  value={server.name}
                  onChange={(e) =>
                    updateServer(server.id, {
                      name: clip(e.target.value, MCP_NAME_MAX, 'mcp-name-max', 'MCP server name'),
                    })
                  }
                  className={errClass(!!errs.name || (server.name || '').length >= MCP_NAME_MAX)}
                  autoFocus
                />
                <FieldMeta error={errs.name} count={(server.name || '').length} max={MCP_NAME_MAX} />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label>
                    URL <span className="text-destructive">*</span>
                  </Label>
                  <HeadingInfo text="The address of the MCP server." />
                </div>
                <Input
                  placeholder="https://mcp.example.com"
                  value={server.url}
                  onChange={(e) =>
                    updateServer(server.id, {
                      url: clip(e.target.value, MCP_URL_MAX, 'mcp-url-max', 'MCP server URL'),
                    })
                  }
                  className={errClass(!!errs.url || (server.url || '').length >= MCP_URL_MAX)}
                />
                <FieldMeta error={errs.url} count={(server.url || '').length} max={MCP_URL_MAX} />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label>Description (optional)</Label>
                  <HeadingInfo text="Tells the agent what this server is for. The server provides its own tools, so you don't define each endpoint." />
                </div>
                <Input
                  placeholder="What this MCP server is used for"
                  value={server.description}
                  onChange={(e) =>
                    updateServer(server.id, {
                      description: clip(e.target.value, descLimit, 'mcp-desc-max', 'Description'),
                    })
                  }
                  className={errClass(!!errs.description || (server.description || '').length >= descLimit)}
                />
                <FieldMeta error={errs.description} count={(server.description || '').length} max={descLimit} />
              </div>
            </div>
          ) : (
            <div
              key={server.id}
              className="flex items-center justify-between rounded-md border px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {server.name || `Server ${index + 1}`}
                </p>
                {server.url && (
                  <p className="truncate text-xs text-muted-foreground">{server.url}</p>
                )}
              </div>
              <span className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-primary hover:text-primary"
                  onClick={() => setOpenId(server.id)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => removeServer(server.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </span>
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
}
